/**
 * Pure dashboard adapter (Phase 06.1, DASH-01).
 *
 * D-02 reconciled to SPB-only past-due per Blocker B-1 — InvoiceRow has no
 * due date; past-due is read from the server's `progressBilling.paymentStatus`
 * and never synthesized from a per-invoice date (Pitfall P-3). This matches
 * all of Phase 5.
 *
 * This module is the testable business core of the phase: it maps fetched
 * store data to the calm-vs-past-due balance split and selects exactly one of
 * the §8 dashboard states. It is PURE — no Vue, no fetch, no styling, no
 * red/neutral color logic (that lives in the components). It logs nothing —
 * financial cents never leave this unit (T-06.1-03).
 *
 * The hero pill delegates to {@link mapStatusToPillKey} (D-05 / B-2) so the
 * dashboard reuses the shared dispatch taxonomy rather than inventing a
 * net-new enum. Unknown wire values degrade to a renderable branch, never
 * throwing and never blanking the balance (T-06.1-02 / D-10), mirroring the
 * defensive-default discipline of `mapStatusToLifecycle`.
 */

import type { InvoiceRow } from '../api/services/interfaces/InvoiceService';
import type {
  DashboardBalance,
  DashboardHeroInput,
  DashboardState,
  DashboardStateInput,
} from '../types/dashboard';
import { mapStatusToPillKey, type StatusPillKey } from './appointment-status';

/**
 * Split a fetched invoice report into the calm outstanding total and the
 * SPB-only past-due total.
 *
 * Outstanding (calm): the verbatim `unpaidTotalCents` reducer from
 * useInvoicesStore.ts — sum `balanceCents` over rows where `balanceCents > 0`.
 * The ids of those rows become `unpaidIds`.
 *
 * Past-due (B-1): SPB-only. Sum `progressBilling.pastDueAmountCents` ONLY over
 * rows whose `progressBilling.paymentStatus === 'past_due'` (the verbatim
 * `hasPastDue` predicate, extended to a sum). A standard invoice — even one
 * with a large balance and no progressBilling — contributes to outstanding but
 * NEVER to past-due. There is no per-invoice due date to read; past-due is a
 * server-derived SPB concept (P-3).
 *
 * Degenerate input (empty report, missing pastDueAmountCents) yields a
 * renderable all-zero balance and never throws (D-10).
 */
export function deriveBalance(rows: InvoiceRow[]): DashboardBalance {
  const report = Array.isArray(rows) ? rows : [];

  const outstandingCents = report.reduce(
    (sum, row) => (row.balanceCents > 0 ? sum + row.balanceCents : sum),
    0,
  );

  const unpaidIds = report
    .filter((row) => row.balanceCents > 0)
    .map((row) => String(row.id));

  const pastDueCents = report.reduce((sum, row) => {
    if (row.progressBilling?.paymentStatus === 'past_due') {
      return sum + (row.progressBilling.pastDueAmountCents ?? 0);
    }
    return sum;
  }, 0);

  return {
    outstandingCents,
    pastDueCents,
    isPastDue: pastDueCents > 0,
    unpaidIds,
  };
}

/**
 * Derive the hero status pill for the active appointment by delegating to the
 * shared {@link mapStatusToPillKey} (D-05 / B-2 — no net-new dispatch enum).
 *
 * Balance and `paid_at` are irrelevant to the dashboard hero pill (the hero
 * morphs on dispatch progression only), so they are passed as 0 / null. The
 * dispatch timestamps drive the enroute/arrived morph; pre-dispatch statuses
 * (pending/assigned/accepted) collapse to the Scheduled morph (A3).
 */
export function deriveHeroPillKey(appt: DashboardHeroInput): StatusPillKey {
  return mapStatusToPillKey(
    appt.status,
    0,
    null,
    appt.dispatch_time,
    appt.arrival_time,
  );
}

/**
 * Select exactly one {@link DashboardState} from the fetched data, per the §8
 * matrix.
 *
 * Precedence (§8):
 *   0. Not yet settled (loading)         → 'loading' — the NON-TERMINAL sentinel
 *      wins over every terminal branch so an empty pre-fetch snapshot is never
 *      read as first-time/no-activity during the load window (Plan 08).
 *   1. No history at all                 → 'first-time'   (§7 welcome state).
 *   2. Active appt dispatched/on-site    → 'en-route' / 'arrived' — the hero's
 *      dispatch morph wins over the balance state for the top-level state
 *      (the balance/past-due banner still renders independently in the view).
 *   3. Past-due balance                  → 'past-due'.
 *   4. Outstanding (calm) balance        → 'balance-due'.
 *   5. Otherwise (history, no active     → 'no-activity'.
 *      dispatch, zero balance)
 *
 * Membership is NOT decided here — it is an orthogonal boolean overlay carried
 * separately on the view model.
 *
 * Defensive default: an unknown appointment status produces a non-dispatch
 * hero pill (via mapStatusToPillKey's `pending` fallthrough), so it never
 * forces an en-route/arrived state and the function falls through to the
 * balance branches. Never throws, never blanks the balance (T-06.1-02 / D-10).
 */
export function deriveDashboardState(
  input: DashboardStateInput,
): DashboardState {
  if (input.loading) return 'loading';

  if (!input.hasHistory) return 'first-time';

  if (input.activeAppointment) {
    const pill = deriveHeroPillKey(input.activeAppointment);
    if (pill === 'arrived') return 'arrived';
    if (pill === 'enroute') return 'en-route';
  }

  if (input.balance.isPastDue) return 'past-due';
  if (input.balance.outstandingCents > 0) return 'balance-due';

  return 'no-activity';
}
